import React from 'react';
import { Link } from '../i18n/LocaleLink';
import { useReveal } from '../hooks/useReveal';
import { useLocale } from '../i18n/LocaleContext';

// Homepage About teaser — same header rhythm as Selected Works / Testimonials:
// large Anton title left, supporting line right, then photo + body.
const HomeAboutTeaser = () => {
  const ref = useReveal();
  const { t } = useLocale();

  return (
    <section ref={ref} className="bg-surface py-20 sm:py-24 lg:py-32 border-t border-hairline">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-8 items-start justify-start mb-12 sm:mb-16 lg:mb-20">
          <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between w-full gap-8">
            <div className="reveal-up font-anton text-[18vw] sm:text-[140px] lg:text-[200px] text-mainkleur leading-[0.9] tracking-[-4px] sm:tracking-[-6px] lg:tracking-[-8px] w-full lg:w-[761px]">
              <p className="mb-0">{t('home.aboutLine1')}</p>
              <p>{t('home.aboutLine2')}</p>
            </div>
            <div className="reveal-up font-space font-normal text-[15px] sm:text-[17px] lg:text-[20px] text-muted text-left lg:text-right w-full lg:w-[638px]">
              <p className="leading-[1.35] sm:leading-[1.2] lg:leading-[1.25] m-0">
                {t('home.aboutEyebrow')}
              </p>
            </div>
          </div>
          <div className="reveal-up h-px w-full bg-mainkleur/20" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-10 lg:gap-16 items-center">
          <div className="reveal-up relative order-1 lg:order-none">
            <div className="relative overflow-hidden rounded-[24px] border border-hairline">
              <img
                src="/images/about-pag-sm.webp"
                alt={t('common.portraitAlt')}
                width={960}
                height={1200}
                loading="lazy"
                decoding="async"
                className="w-full h-[340px] sm:h-[440px] lg:h-[520px] object-cover"
              />
            </div>
          </div>

          <div className="flex flex-col gap-8">
            <h2 className="reveal-up font-bricolage font-extrabold text-mainkleur text-[34px] sm:text-[46px] lg:text-[58px] leading-[1.02] tracking-[-1.5px]">
              {t('home.aboutHeading')}
            </h2>

            <p className="reveal-up font-space text-muted text-[16px] sm:text-[18px] lg:text-[20px] leading-[1.55] max-w-[640px]">
              {t('home.aboutBody')}
            </p>

            <p className="reveal-up font-space text-mainkleur/70 text-[14px] sm:text-[15px] leading-[1.5]">
              Based in Bilzen-Hoeselt
              <span className="text-mainkleur/30 mx-2" aria-hidden="true">·</span>
              Op de brikkenoven 6, 3740
            </p>

            <div className="reveal-up">
              <Link
                to="/about"
                className="group inline-flex items-center gap-3 font-bricolage font-semibold text-mainkleur text-lg sm:text-xl uppercase tracking-tight hover:text-[#f97b22] transition-colors duration-300"
              >
                <span>{t('home.moreAbout')}</span>
                <span className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-mainkleur group-hover:bg-mainkleur group-hover:text-licht flex items-center justify-center transition-all duration-300 group-hover:rotate-[-45deg]">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeAboutTeaser;
